import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatBytes } from "@/components/grc/EvidenceVault";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, FileText, X, ChevronsUpDown, Check, Cloud } from "lucide-react";

const ACCEPT = ".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt,.csv,.json";
const NONE = "__none__";

const FRIENDLY = {
  too_large: "This file is over the 50 MB limit. Split or compress it and try again.",
  type_not_allowed: "This file type can't be stored as evidence.",
  content_mismatch: "This file's content doesn't match its extension, so it was blocked for safety.",
  storage_unavailable: "Evidence storage is temporarily unavailable. Please try again in a few minutes.",
  library_settings: "The evidence library's settings blocked this upload. Your administrator has been alerted.",
  label_protected: "A sensitivity label on this file prevents it from being stored. Remove the label or ask your administrator.",
};

export const uploadErrorMessage = (e) => {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object") return d.message || FRIENDLY[d.code] || "Upload failed";
  if (e?.response?.status === 413) return FRIENDLY.too_large;
  return d || "Upload failed";
};

const PathCrumb = ({ orgName, assessmentLabel, control }) => (
  <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-1" data-testid="evidence-upload-path">
    {[orgName || "Your organisation", assessmentLabel, control?.pillar || "Pillar", control?.question_id || "Control"].map((p, i) => (
      <span key={i} className="flex items-center gap-1">{i > 0 && <span className="text-slate-300">›</span>}<span className="font-medium text-slate-700">{p}</span></span>
    ))}
  </div>
);

export const UploadEvidenceModal = ({ assessmentId, controls, open, onClose, onUploaded, storageBackend, orgName }) => {
  const [file, setFile] = useState(null);
  const [controlId, setControlId] = useState(null);
  const [controlPickerOpen, setControlPickerOpen] = useState(false);
  const [findings, setFindings] = useState([]);
  const [findingId, setFindingId] = useState(NONE);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [description, setDescription] = useState("");
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (open) {
      setFile(null); setControlId(null); setFindingId(NONE);
      setTags([]); setTagInput(""); setExpiryDate(""); setDescription(""); setProgress(null);
      api.get(`/grc/${assessmentId}/findings`).then(({ data }) => setFindings(data.findings || [])).catch(() => {});
    }
  }, [open, assessmentId]);

  const onDrop = useCallback((accepted) => {
    if (accepted?.[0]) setFile(accepted[0]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: false });

  const selectedControl = controls.find((c) => c.control_id === controlId);
  const controlLabel = (c) =>
    `${c.question_id} · ${c.pillar}${c.question_text ? ` · ${c.question_text.slice(0, 40)}` : ""}`;

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  };

  const upload = async () => {
    if (!file) return toast.error("Select a file first");
    if (!controlId) return toast.error("Link the evidence to a control");
    const form = new FormData();
    form.append("file", file);
    form.append("metadata", JSON.stringify({
      control_id: controlId,
      finding_id: findingId !== NONE ? findingId : undefined,
      tags,
      expiry_date: expiryDate || undefined,
      description: description || undefined,
    }));
    setProgress(0);
    try {
      await api.post("/grc/evidence/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)),
      });
      toast.success(`Evidence uploaded and linked to ${selectedControl?.question_id || "control"}`);
      onUploaded();
    } catch (e) {
      toast.error(uploadErrorMessage(e));
      setProgress(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="upload-evidence-modal">
        <DialogHeader>
          <DialogTitle className="text-base text-[#003366]">Upload Evidence</DialogTitle>
          <DialogDescription className="text-xs">
            PDF, DOCX, XLSX, PNG, JPG, TXT, CSV or JSON — max 50 MB.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 space-y-1.5">
          <PathCrumb orgName={orgName} assessmentLabel={`Assessment ${String(assessmentId || "").slice(0, 8)}`} control={selectedControl} />
          {storageBackend === "sharepoint" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#1E5EFF]/10 text-[#1E5EFF] px-2 py-0.5 text-[10px] font-medium" data-testid="evidence-storage-chip">
              <Cloud size={11} /> Stored in Microsoft India Datacentre
            </span>
          )}
        </div>

        <div className="space-y-4 py-1">
          <div
            {...getRootProps()}
            data-testid="evidence-dropzone"
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
              isDragActive ? "border-[#008080] bg-blue-50" : "border-slate-300 hover:border-slate-400"
            }`}
          >
            <input {...getInputProps()} accept={ACCEPT} data-testid="evidence-file-input" />
            <UploadCloud size={28} className="mx-auto text-slate-400 mb-2" />
            <p className="text-xs text-slate-500">
              {isDragActive ? "Drop the file here…" : "Drag & drop a file here, or click to browse"}
            </p>
          </div>

          {file && (
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2" data-testid="evidence-file-preview">
              <FileText size={18} className="text-[#008080]" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate">{file.name}</div>
                <div className="text-[10px] text-slate-400">{formatBytes(file.size)} · {file.type || "unknown type"}</div>
              </div>
              <button onClick={() => setFile(null)} className="text-slate-400 hover:text-red-500" data-testid="evidence-file-remove">
                <X size={14} />
              </button>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Link to Control *</Label>
            <Popover open={controlPickerOpen} onOpenChange={setControlPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full h-9 justify-between text-xs font-normal" data-testid="evidence-control-picker">
                  {selectedControl ? controlLabel(selectedControl) : "Search controls…"}
                  <ChevronsUpDown size={13} className="opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[420px] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search Q#, pillar or title…" className="text-xs" data-testid="evidence-control-search" />
                  <CommandList>
                    <CommandEmpty>No controls found.</CommandEmpty>
                    <CommandGroup>
                      {controls.map((c) => (
                        <CommandItem
                          key={c.control_id}
                          value={controlLabel(c)}
                          onSelect={() => { setControlId(c.control_id); setControlPickerOpen(false); }}
                          className="text-xs"
                          data-testid={`evidence-control-option-${c.question_id}`}
                        >
                          <Check size={13} className={`mr-2 ${controlId === c.control_id ? "opacity-100" : "opacity-0"}`} />
                          {controlLabel(c)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Link to Finding (optional)</Label>
            <Select value={findingId} onValueChange={setFindingId}>
              <SelectTrigger className="h-9 text-xs" data-testid="evidence-finding-select">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {findings.map((f) => (
                  <SelectItem key={f.finding_id} value={f.finding_id} className="text-xs">
                    [{f.severity}] {(f.title || "").slice(0, 50)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Tags</Label>
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1.5" data-testid="evidence-tags-field">
              {tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-[10px] text-slate-700">
                  {t}
                  <button onClick={() => setTags(tags.filter((x) => x !== t))} className="hover:text-red-500">
                    <X size={10} />
                  </button>
                </span>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); }
                }}
                onBlur={addTag}
                placeholder={tags.length === 0 ? "Type a tag, press Enter" : ""}
                className="flex-1 min-w-[100px] text-xs outline-none bg-transparent"
                data-testid="evidence-tag-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Expiry Date (optional)</Label>
              <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
                className="h-9 text-xs" data-testid="evidence-expiry-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this document evidence?"
              className="text-xs min-h-[60px]" data-testid="evidence-description" />
          </div>

          {progress !== null && (
            <div className="space-y-1" data-testid="evidence-upload-progress">
              <Progress value={progress} className="h-2" />
              <p className="text-[10px] text-slate-400 text-right">{progress}%</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="evidence-upload-cancel">Cancel</Button>
          <Button
            onClick={upload}
            disabled={progress !== null && progress < 100}
            className="bg-[#008080] hover:bg-[#1a51db]"
            data-testid="evidence-upload-submit"
          >
            {progress !== null ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
